import { Check, Loader2, Sparkles } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SubscriptionPurchaseService } from "@/lib/subscription/subscription-purchase.service";
import { SubscriptionService } from "@/lib/subscription/subscription.service";
import { useCoinBalance } from "@/lib/coin/coin.store";
import { useSubscription } from "@/lib/subscription/useSubscription.hook";
import { AnalyticsService } from "@/lib/analytics/analytics.service";
import {
	TrialService,
	EndTrialNowError,
} from "@/lib/subscription/trial.service";
import { useAuth } from "@/lib/contexts/auth.context";
import type { PlanTier } from "@/lib/platformManager/platformManager.service";
import {
	preOpenCheckoutWindow,
	watchCheckoutWindowClose,
} from "@/lib/stripe/checkoutWindow";

type SubscriptionStatusKey =
	| "ACTIVE"
	| "TRIALING"
	| "PAST_DUE"
	| "UNPAID"
	| "CANCELED"
	| "INCOMPLETE"
	| "INCOMPLETE_EXPIRED"
	| "NONE";

const statusStyles: Record<SubscriptionStatusKey, string> = {
	ACTIVE: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
	TRIALING: "bg-sky-500/10 text-sky-300 border-sky-500/30",
	PAST_DUE: "bg-amber-500/10 text-amber-300 border-amber-500/30",
	UNPAID: "bg-destructive/10 text-destructive border-destructive/30",
	CANCELED: "bg-muted/60 text-muted-foreground border-muted/60",
	INCOMPLETE: "bg-muted/60 text-muted-foreground border-muted/60",
	INCOMPLETE_EXPIRED: "bg-muted/60 text-muted-foreground border-muted/60",
	NONE: "bg-muted/60 text-muted-foreground border-muted/60",
};

interface Plan {
	backendId: string;
	id: string;
	tier: PlanTier;
	name: string;
	price: number;
	originalPrice?: number;
	launchOffer?: string;
	tagline: string;
	features: string[];
	highlight?: boolean;
	cta: string;
	stripePriceId?: string; // Stripe Price ID for paid plans
}

const INACTIVE_STATUSES = [
	"NONE",
	"CANCELED",
	"UNPAID",
	"INCOMPLETE",
	"INCOMPLETE_EXPIRED",
] as const;

const SimplePricing = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { subscriptionStatus, currentPlanId } = useSubscription();
	const { refreshUserProfile } = useAuth();
	const [plans, setPlans] = useState<Plan[]>([]);
	const [loading, setLoading] = useState(true);
	const [purchasing, setPurchasing] = useState<string | null>(null);
	const [searchParams, setSearchParams] = useSearchParams();
	const { refresh: refreshBalance } = useCoinBalance();
	// Optimistic override flips us out of TRIALING immediately after a
	// successful "Go paid" so the UI does not wait for the Stripe webhook.
	const [optimisticActive, setOptimisticActive] = useState(false);
	const rawStatus = (
		(subscriptionStatus as SubscriptionStatusKey | undefined) ?? "NONE"
	).toUpperCase() as SubscriptionStatusKey;
	const normalizedStatus: SubscriptionStatusKey =
		optimisticActive && rawStatus === "TRIALING" ? "ACTIVE" : rawStatus;

	// Fetch plans from backend
	useEffect(() => {
		const fetchPlans = async () => {
			try {
				setLoading(true);
				const backendPlans = await SubscriptionService.getPlans();

				// Map backend plans to frontend Plan interface
				// Backend already filters by showInFrontend, so we just map them
				// Extract multilingual fields based on current language
				const currentLang = i18next.language?.split("-")[0] || "en"; // Get base language (e.g., "en" from "en-US")
				const mappedPlans: Plan[] = backendPlans
					.map((plan) => {
						// Extract description (multilingual JSON)
						const description =
							typeof plan.description === "object" &&
							plan.description !== null &&
							!Array.isArray(plan.description)
								? (plan.description as Record<string, string>)[currentLang] ||
									(plan.description as Record<string, string>).en ||
									""
								: "";

						// Extract features (multilingual JSON)
						const features =
							typeof plan.features === "object" &&
							plan.features !== null &&
							!Array.isArray(plan.features)
								? (plan.features as Record<string, string[]>)[currentLang] ||
									(plan.features as Record<string, string[]>).en ||
									[]
								: Array.isArray(plan.features)
									? plan.features
									: [];

						// Extract CTA (multilingual JSON)
						let cta: string = "Get Started";
						if (
							typeof plan.cta === "object" &&
							plan.cta !== null &&
							!Array.isArray(plan.cta)
						) {
							cta =
								(plan.cta as Record<string, string>)[currentLang] ||
								(plan.cta as Record<string, string>).en ||
								"Get Started";
						} else if (typeof plan.cta === "string") {
							cta = plan.cta;
						}

						// Extract launchOffer (multilingual JSON)
						const launchOffer =
							typeof plan.launchOffer === "object" &&
							plan.launchOffer !== null &&
							!Array.isArray(plan.launchOffer)
								? (plan.launchOffer as Record<string, string>)[currentLang] ||
									(plan.launchOffer as Record<string, string>).en ||
									undefined
								: undefined;

						return {
							backendId: plan.id,
							id: plan.name.toLowerCase(),
							tier: (plan.tier ?? "UNKNOWN") as PlanTier,
							name: plan.name,
							price: plan.price ?? 0,
							originalPrice: plan.originalPrice ?? undefined,
							launchOffer,
							tagline: description,
							features,
							highlight: plan.highlight ?? false,
							cta,
							stripePriceId: plan.stripePriceId ?? undefined,
						};
					})
					// Sort: Free, Starter, Creator
					.sort((a, b) => {
						const order: Record<PlanTier, number> = {
							FREE: 0,
							STARTER: 1,
							CREATOR: 2,
							DOWNSWELL: 3,
							UNKNOWN: 99,
						};
						return order[a.tier] - order[b.tier];
					});

				// Hide the free plan — only show paid plans with trial
				const paidPlans = mappedPlans.filter((p) => p.price > 0);
				setPlans(paidPlans);

				// Track that the user viewed each plan
				for (const plan of paidPlans) {
					AnalyticsService.track("plan_viewed", {
						plan_name: plan.name,
						price_id: plan.stripePriceId,
						price: plan.price,
						currency: "EUR",
					});
				}
			} catch (error) {
				console.error("Failed to fetch plans:", error);
				toast.error(t("pricing.subscription.toasts.loadFailed"));
				// Fallback to empty array - component will show nothing
				setPlans([]);
			} finally {
				setLoading(false);
			}
		};

		fetchPlans();
	}, [t]);

	// Check for payment success/cancel in URL
	useEffect(() => {
		const payment = searchParams.get("payment");
		const sessionId = searchParams.get("session_id");

		// If we're in a popup window (opened by parent), send message and close
		if (window.opener && !window.opener.closed) {
			if (payment === "success" && sessionId) {
				window.opener.postMessage(
					{ type: "STRIPE_PAYMENT_SUCCESS", sessionId },
					window.location.origin,
				);
				window.close();
				return;
			} else if (payment === "cancelled") {
				window.opener.postMessage(
					{ type: "STRIPE_PAYMENT_CANCELLED" },
					window.location.origin,
				);
				window.close();
				return;
			}
		}

		// If we're in the main window (not popup), handle normally
		if (payment === "success" && sessionId) {
			toast.success(t("pricing.subscription.toasts.paymentSuccess"));
			refreshBalance();
			setSearchParams({});
		} else if (payment === "cancelled") {
			toast.error(t("pricing.subscription.toasts.paymentCancelled"));
			setSearchParams({});
		}
	}, [searchParams, setSearchParams, refreshBalance, t]);

	const stopCheckoutWatchRef = useRef<(() => void) | null>(null);
	useEffect(() => () => stopCheckoutWatchRef.current?.(), []);

	// Listen for messages from popup window
	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			if (event.origin !== window.location.origin) {
				return;
			}

			if (event.data.type === "STRIPE_PAYMENT_SUCCESS") {
				const sessionId = event.data.sessionId;
				setPurchasing(null);
				// Redirect to success page with session_id
				if (sessionId) {
					navigate(`/pricing/success?session_id=${sessionId}`);
				} else {
					navigate("/pricing/success");
				}
			} else if (event.data.type === "STRIPE_PAYMENT_CANCELLED") {
				toast.error(t("pricing.subscription.toasts.paymentCancelled"));
				setPurchasing(null);
			}
		};

		window.addEventListener("message", handleMessage);
		return () => window.removeEventListener("message", handleMessage);
	}, [navigate, t]);

	const isCreatorTrialingCurrent = (plan: Plan) =>
		Boolean(
			currentPlanId &&
				plan.backendId === currentPlanId &&
				rawStatus === "TRIALING" &&
				plan.tier === "CREATOR",
		);

	const handlePlanClick = async (plan: Plan) => {
		if (isCreatorTrialingCurrent(plan)) {
			try {
				setPurchasing(plan.backendId);
				await TrialService.endTrialNow();
				// Flip UI optimistically — the Stripe webhook will eventually
				// confirm and `refreshUserProfile` will reconcile state.
				setOptimisticActive(true);
				toast.success(t("trial.cap.toasts.activated"));
				await refreshBalance();
				// Best-effort profile refresh; ignore failures so the optimistic
				// state stays in place until the webhook catches up.
				refreshUserProfile().catch((profileErr) => {
					console.warn("Profile refresh after endTrialNow failed:", profileErr);
				});
			} catch (error) {
				console.error("Failed to end trial / activate paid billing:", error);
				if (
					error instanceof EndTrialNowError &&
					error.code === "NO_PAYMENT_METHOD"
				) {
					toast.error(t("trial.cap.toasts.noPaymentMethod"));
				} else {
					toast.error(t("trial.cap.toasts.activateFailed"));
				}
			} finally {
				setPurchasing(null);
			}
			return;
		}

		if (currentPlanId && plan.backendId === currentPlanId) {
			toast.info(t("pricing.subscription.cta.current"));
			return;
		}

		// Free plan - no purchase needed
		if (plan.id === "free" || plan.price === 0) {
			toast.info(t("pricing.subscription.toasts.freePlan"));
			return;
		}

		// Paid plans must have a stripePriceId
		if (!plan.stripePriceId) {
			console.error("Paid plan missing stripePriceId:", plan);
			toast.error(t("pricing.subscription.toasts.startPurchaseFailed"));
			return;
		}

		try {
			setPurchasing(plan.backendId);
			const shouldPreOpenCheckout =
				!subscriptionStatus ||
				!currentPlanId ||
				INACTIVE_STATUSES.includes(
					(
						subscriptionStatus as string
					).toUpperCase() as (typeof INACTIVE_STATUSES)[number],
				);
			const preOpenedCheckoutWindow = shouldPreOpenCheckout
				? preOpenCheckoutWindow("stripe-checkout")
				: null;
			SubscriptionPurchaseService.setPreOpenedCheckoutWindow(
				preOpenedCheckoutWindow,
			);

			const checkoutWindow =
				await SubscriptionPurchaseService.purchaseSubscription(
					plan.stripePriceId,
					currentPlanId ?? undefined,
					subscriptionStatus ?? undefined,
				);
			// Note: Success handling is done in purchaseSubscription method
			// For checkout: popup window handles success
			// For upgrade/downgrade: page reloads to show updated subscription

			// A checkout dismissed via the window chrome posts no message back,
			// which would otherwise leave this plan's button disabled for good.
			stopCheckoutWatchRef.current?.();
			stopCheckoutWatchRef.current = watchCheckoutWindowClose(
				checkoutWindow,
				() => setPurchasing(null),
			);
		} catch (error: any) {
			console.error("Failed to initiate subscription purchase:", error);

			// Check if it's an upgrade/downgrade error
			const errorMessage = error?.message || error?.toString() || "";
			if (errorMessage.includes("upgrade")) {
				toast.error(t("pricing.subscription.toasts.upgradeFailed"));
			} else if (errorMessage.includes("downgrade")) {
				toast.error(t("pricing.subscription.toasts.downgradeFailed"));
			} else {
				toast.error(t("pricing.subscription.toasts.startPurchaseFailed"));
			}
			setPurchasing(null);
		}
	};

	return (
		<div className="w-full bg-background py-10 md:py-16">
			<div className="mx-auto max-w-6xl px-4 md:px-6">
				{/* Header */}
				<div className="text-center max-w-6xl mx-auto mb-12">
					<h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4 text-foreground">
						{t("pricing.subscription.headline")}
					</h2>
					<p className="text-muted-foreground mb-2">
						{t("pricing.subscription.subheadline")}
					</p>
				</div>

				{/* Plans grid */}
				{loading ? (
					<div className="flex justify-center items-center py-12">
						<Loader2 className="h-8 w-8 animate-spin text-primary" />
					</div>
				) : plans.length === 0 ? (
					<div className="text-center py-12">
						<p className="text-muted-foreground">
							{t("pricing.subscription.noPlans")}
						</p>
					</div>
				) : (
					<div className="grid gap-6 md:grid-cols-2 items-stretch max-w-3xl mx-auto">
						{plans.map((plan) => {
							const isHighlight = plan.highlight;
							const hasDiscount =
								plan.originalPrice && plan.originalPrice > plan.price;
							const isCurrentPlan = currentPlanId === plan.backendId;
							const trialingCurrentCreator =
								isCurrentPlan &&
								rawStatus === "TRIALING" &&
								plan.tier === "CREATOR" &&
								!optimisticActive;
							// Trial is restricted to Creator only — Starter is direct-paid.
							const isCreatorPlan = plan.tier === "CREATOR";
							const isTrialEligible =
								normalizedStatus === "NONE" && isCreatorPlan;
							const statusClass = statusStyles[normalizedStatus];
							const statusLabel =
								normalizedStatus === "NONE"
									? null
									: t(`pricing.subscription.status.${normalizedStatus}`);
							const ctaLabel = trialingCurrentCreator
								? t("pricing.subscription.cta.endTrialAndPay")
								: isCurrentPlan
									? t("pricing.subscription.cta.current")
									: plan.price > 0 && isTrialEligible
										? t("pricing.subscription.cta.startTrial")
										: plan.cta || t("pricing.subscription.cta.default");

							return (
								<div
									key={plan.backendId}
									className={[
										"flex flex-col rounded-2xl border bg-card p-6 md:p-7 relative transition-all hover:shadow-lg",
										isHighlight
											? "border-primary shadow-xl shadow-primary/10 md:scale-[1.05]"
											: "border-border",
									].join(" ")}
								>
									{/* Highlight badge */}
									{isHighlight && (
										<span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center rounded-full border border-primary bg-primary text-primary-foreground px-3 py-1 text-xs font-medium">
											{t("pricing.subscription.mostPopular")}
										</span>
									)}

									{/* Plan header */}
									<div className="mb-4">
										<h3 className="text-xl font-semibold text-foreground">
											{plan.name}
										</h3>
										<p className="text-sm text-muted-foreground mt-1">
											{plan.tagline}
										</p>
										{isCurrentPlan && (
											<div className="mt-3 flex flex-wrap items-center gap-2">
												<span className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium bg-primary/10 border-primary/30 text-primary">
													{t("pricing.subscription.currentPlanBadge")}
												</span>
												{statusLabel && (
													<span
														className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium ${statusClass}`}
													>
														{statusLabel}
													</span>
												)}
											</div>
										)}
									</div>

									{/* Pricing */}
									<div className="mb-6">
										<div className="flex items-baseline gap-2">
											{hasDiscount && (
												<span className="text-lg text-muted-foreground line-through">
													€{plan.originalPrice?.toFixed(2)}
												</span>
											)}
											<span className="text-3xl md:text-4xl font-semibold text-foreground">
												€{plan.price.toFixed(2)}
											</span>
											{plan.price > 0 && (
												<span className="text-sm text-muted-foreground">
													{t("pricing.subscription.perMonth")}
												</span>
											)}
										</div>
										{hasDiscount && plan.originalPrice && plan.launchOffer && (
											<p className="text-sm mt-1 text-primary font-medium">
												{t("pricing.subscription.launchOffer", {
													percent: Math.round(
														((plan.originalPrice - plan.price) /
															plan.originalPrice) *
															100,
													),
												})}
											</p>
										)}
										{plan.price === 0 && (
											<p className="text-xs mt-1 text-muted-foreground">
												{t("pricing.subscription.noCardNeeded")}
											</p>
										)}
										{plan.price > 0 && !isCurrentPlan && isTrialEligible && (
											<p className="text-xs mt-1.5 text-emerald-500 font-medium flex items-center gap-1">
												<Sparkles className="h-3 w-3" />
												{t("pricing.subscription.trialNote")}
											</p>
										)}
									</div>

									{/* Features */}
									<ul className="space-y-3 text-sm text-card-foreground mb-6 flex-1">
										{plan.features.map((feature) => (
											<li key={feature} className="flex items-start gap-2">
												<Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
												<span>{feature}</span>
											</li>
										))}
									</ul>

									{/* CTA Button */}
									<button
										type="button"
										onClick={() => handlePlanClick(plan)}
										disabled={
											purchasing === plan.backendId ||
											(isCurrentPlan && !trialingCurrentCreator)
										}
										aria-busy={purchasing === plan.backendId}
										className={[
											"w-full rounded-full px-4 py-3 text-sm font-medium transition-all",
											"disabled:opacity-50 disabled:cursor-not-allowed",
											isHighlight
												? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md hover:shadow-lg"
												: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
										].join(" ")}
									>
										{purchasing === plan.backendId ? (
											<>
												<Loader2 className="inline-block mr-2 h-4 w-4 animate-spin" />
												{t("common.processing")}
											</>
										) : (
											<>
												{ctaLabel}{" "}
												{(!isCurrentPlan || trialingCurrentCreator) && "→"}
											</>
										)}
									</button>

									{isHighlight && (
										<p className="mt-3 text-center text-[11px] text-muted-foreground">
											{t("pricing.subscription.notes.highlightFooter")}
										</p>
									)}
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
};

export default SimplePricing;
